/**
 * Image shapes — a picture cut to a shape (circle, hexagon, star…).
 *
 * The outline is a list of points in the picture's own box (px). The editor
 * clips with them (CSS clip-path) and the renderer masks with the SAME points
 * (helper/banner_backend.py → image_shape_points), so the video matches the
 * preview. Keep the two in step.
 */

export const IMAGE_SHAPES = [
  { value: 'rect', label: 'Rectangle' },
  { value: 'circle', label: 'Circle' },
  { value: 'pill', label: 'Pill' },
  { value: 'arch', label: 'Arch' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'hexagon', label: 'Hexagon' },
  { value: 'triangle', label: 'Triangle' },
  { value: 'star', label: 'Star' },
];

const ARC_STEPS = 12;
const ROUND_STEPS = 72;

/** Rounded rectangle; radii = [top-left, top-right, bottom-right, bottom-left]. */
function roundedRect(w, h, radii) {
  const pts = [];
  const corner = (cx, cy, r, a0) => {
    if (r <= 0) {
      pts.push([cx + r * Math.cos(a0), cy + r * Math.sin(a0)]);
      return;
    }
    for (let i = 0; i <= ARC_STEPS; i += 1) {
      const a = a0 + (Math.PI / 2) * (i / ARC_STEPS);
      pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
  };
  const [tl, tr, br, bl] = radii;
  if (tl > 0) corner(tl, tl, tl, Math.PI); else pts.push([0, 0]);
  if (tr > 0) corner(w - tr, tr, tr, -Math.PI / 2); else pts.push([w, 0]);
  if (br > 0) corner(w - br, h - br, br, 0); else pts.push([w, h]);
  if (bl > 0) corner(bl, h - bl, bl, Math.PI / 2); else pts.push([0, h]);
  return pts;
}

/** Outline points [[x, y], …] in px for a box of w × h; null = plain rectangle. */
export function imageShapePoints(kind, w, h) {
  if (!(w > 0 && h > 0)) return null;
  switch (kind) {
    case 'circle': {
      const pts = [];
      for (let i = 0; i < ROUND_STEPS; i += 1) {
        const a = (2 * Math.PI * i) / ROUND_STEPS;
        pts.push([w / 2 + (w / 2) * Math.cos(a), h / 2 + (h / 2) * Math.sin(a)]);
      }
      return pts;
    }
    case 'pill': {
      const r = Math.min(w, h) / 2;
      return roundedRect(w, h, [r, r, r, r]);
    }
    case 'arch': {
      const r = Math.min(w / 2, h);
      return roundedRect(w, h, [r, r, 0, 0]);
    }
    case 'diamond':
      return [[w / 2, 0], [w, h / 2], [w / 2, h], [0, h / 2]];
    case 'hexagon':
      return [[w * 0.25, 0], [w * 0.75, 0], [w, h / 2], [w * 0.75, h], [w * 0.25, h], [0, h / 2]];
    case 'triangle':
      return [[w / 2, 0], [w, h], [0, h]];
    case 'star': {
      const pts = [];
      for (let i = 0; i < 10; i += 1) {
        const a = -Math.PI / 2 + (Math.PI * i) / 5;
        const k = i % 2 === 0 ? 1 : 0.45;
        pts.push([w / 2 + (w / 2) * k * Math.cos(a), h / 2 + (h / 2) * k * Math.sin(a)]);
      }
      return pts;
    }
    default:
      return null;
  }
}

export function hasImageShape(layer) {
  return layer?.type === 'image' && IMAGE_SHAPES.some((s) => s.value === layer.imageShape) && layer.imageShape !== 'rect';
}

/** CSS clip-path for the shape (percent of the box). */
export function imageShapeClipPath(kind, w, h) {
  const pts = imageShapePoints(kind, w, h);
  if (!pts) return undefined;
  return `polygon(${pts.map(([x, y]) => `${((x / w) * 100).toFixed(3)}% ${((y / h) * 100).toFixed(3)}%`).join(', ')})`;
}

/** SVG points string (px) for the outline / border. */
export function imageShapeSvgPoints(kind, w, h) {
  const pts = imageShapePoints(kind, w, h);
  return pts ? pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ') : '';
}
